import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { FolderIcon, Upload, FolderPlus, ChevronRight, Home, LayoutGrid, List } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import { useAssets } from '@/hooks/useAssets'
import { useDirectUpload } from '@/hooks/useDirectUpload'
import { AssetGrid } from './AssetGrid'
import { AssetList } from './AssetList'
import { CreateFolderDialog } from './CreateFolderDialog'
import type { Asset, AssetsIndexData, AssetOwnerType, AssetItem } from '@/types/assets'

interface AssetsViewProps {
  ownerType: AssetOwnerType
  ownerId: number
  initialData?: AssetsIndexData
  projects?: Array<{ id: number; name: string }>
  selectionMode?: boolean
  onAssetSelect?: (asset: Asset) => void
  className?: string
  isActive?: boolean
}

export function AssetsView({
  ownerType,
  ownerId,
  initialData,
  projects = [],
  selectionMode = false,
  onAssetSelect,
  className = '',
  isActive
}: AssetsViewProps) {
  const { t } = useTranslation('assets')
  const [showCreateFolder, setShowCreateFolder] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid')

  const {
    folders,
    assets,
    currentFolder,
    breadcrumbs,
    isLoading,
    navigateToFolder,
    uploadAsset,
    renameAsset,
    moveAsset,
    copyAssetToProject,
    copyAssetToOrganization,
    deleteAsset,
    createFolder,
    renameFolder,
    deleteFolder
  } = useAssets({ ownerType, ownerId, initialData, isActive })

  const { uploadFiles, uploadProgress } = useDirectUpload()

  // Calculate upload progress counts
  const totalUploads = uploadProgress.size
  const completedUploads = Array.from(uploadProgress.values())
    .filter(p => p.status === 'completed').length

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    if (files.length === 0) return

    setIsUploading(true)
    try {
      const results = await uploadFiles(files)
      for (const result of results) {
        if (result) {
          await uploadAsset(result.signedBlobId, result.filename)
        }
      }
    } finally {
      setIsUploading(false)
      // Reset input
      e.target.value = ''
    }
  }

  const handleCreateFolder = async (name: string) => {
    const folder = await createFolder(name)
    if (folder) {
      setShowCreateFolder(false)
    }
  }

  const handleAssetClick = (asset: Asset) => {
    if (selectionMode && onAssetSelect) {
      onAssetSelect(asset)
    }
  }

  // Combine folders and assets into a single array - folders first, then files
  const items: AssetItem[] = useMemo(() => [
    ...folders.map(f => ({ ...f, itemType: 'folder' as const })),
    ...assets.map(a => ({ ...a, itemType: 'asset' as const }))
  ], [folders, assets])

  return (
    <div className={`flex flex-col h-full ${className}`}>
      {/* Header with breadcrumbs and actions */}
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b">
        {/* Breadcrumbs */}
        <div className="flex flex-wrap items-center gap-1 text-sm">
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2"
            onClick={() => navigateToFolder(null)}
          >
            <Home className="h-4 w-4" />
          </Button>
          {breadcrumbs.map((crumb) => (
            <div key={crumb.id} className="flex items-center">
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2"
                onClick={() => navigateToFolder(crumb.id)}
              >
                {crumb.name}
              </Button>
            </div>
          ))}
        </div>

        {/* View Toggle + Actions */}
        <div className="flex items-center gap-2">
          <ToggleGroup type="single" value={viewMode} onValueChange={(v) => v && setViewMode(v as 'grid' | 'list')}>
            <ToggleGroupItem value="grid" aria-label={t('aria.gridView')} className="h-8 w-8 p-0">
              <LayoutGrid className="h-4 w-4" />
            </ToggleGroupItem>
            <ToggleGroupItem value="list" aria-label={t('aria.listView')} className="h-8 w-8 p-0">
              <List className="h-4 w-4" />
            </ToggleGroupItem>
          </ToggleGroup>

          {!selectionMode && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowCreateFolder(true)}
              >
                <FolderPlus className="h-4 w-4 mr-2" />
                {t('buttons.newFolder')}
              </Button>
              <Button
                size="sm"
                disabled={isUploading}
                asChild
              >
                <label className="cursor-pointer">
                  <Upload className="h-4 w-4 mr-2" />
                  {isUploading && totalUploads > 0
                    ? t('buttons.uploadingProgress', { completed: completedUploads, total: totalUploads })
                    : t('buttons.upload')}
                  <input
                    type="file"
                    multiple
                    className="hidden"
                    onChange={handleFileUpload}
                    accept="image/*,.pdf,.docx,.txt,.md,.csv,.json,.xlsx"
                  />
                </label>
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-4 min-h-0">
        {isLoading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} className="aspect-square rounded-lg" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-center">
            <FolderIcon className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground">
              {currentFolder ? t('emptyStates.folderEmpty') : t('emptyStates.noAssets')}
            </p>
          </div>
        ) : (
          viewMode === 'grid' ? (
            <AssetGrid
              items={items}
              selectionMode={selectionMode}
              onAssetClick={handleAssetClick}
              onFolderClick={navigateToFolder}
              onRenameAsset={!selectionMode ? renameAsset : undefined}
              onDeleteAsset={!selectionMode ? deleteAsset : undefined}
              onRenameFolder={!selectionMode ? renameFolder : undefined}
              onDeleteFolder={!selectionMode ? deleteFolder : undefined}
              onMove={!selectionMode ? moveAsset : undefined}
              onCopyToProject={!selectionMode && ownerType === 'organization' ? copyAssetToProject : undefined}
              onCopyToOrganization={!selectionMode && ownerType === 'project' ? copyAssetToOrganization : undefined}
              projects={projects}
            />
          ) : (
            <AssetList
              items={items}
              selectionMode={selectionMode}
              onAssetClick={handleAssetClick}
              onFolderClick={navigateToFolder}
              onRenameAsset={!selectionMode ? renameAsset : undefined}
              onDeleteAsset={!selectionMode ? deleteAsset : undefined}
              onRenameFolder={!selectionMode ? renameFolder : undefined}
              onDeleteFolder={!selectionMode ? deleteFolder : undefined}
              onMove={!selectionMode ? moveAsset : undefined}
              onCopyToProject={!selectionMode && ownerType === 'organization' ? copyAssetToProject : undefined}
              onCopyToOrganization={!selectionMode && ownerType === 'project' ? copyAssetToOrganization : undefined}
              projects={projects}
            />
          )
        )}
      </div>

      {/* Create Folder Dialog */}
      <CreateFolderDialog
        open={showCreateFolder}
        onOpenChange={setShowCreateFolder}
        onSubmit={handleCreateFolder}
      />
    </div>
  )
}
